import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { httpOk, GET, POST } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'
import { ralToHex, hexToRgb, thicknessFromName } from 'helpers/ral'
import {
	segsOf, blankOf, setSegLen, setSegAng, setDir0, delSegAt,
	hemPointFor, flipHemDirIn, setHemLenIn,
	consumption, validateDrawing, pathWithHems, crossSection,
	HEM_LEN, snapTo, toBend, fromBend, placeLabels, tierMarkup,
	dir0Of, rotatePts, mirrorPts,
	drawingDataUri,
} from './drawingGeometry'

/**
 * AKSESSUAR CHIZMA OYNASI
 *
 * Sotuvchi mijoz oldida profil kesimini chizadi → razvyortka avtomat chiqadi →
 * rasxod rulon bo'lagiga yaxlitlanadi → narx va ombor o'sha zahoti hisoblanadi.
 *
 * Chizma ikki yo'l bilan quriladi:
 *   1) o'ngdagi panelda raqam yozib (uzunlik + burchak)
 *   2) sichqonchani bosib ushlab tortib (uchidagi belgidan ham bo'ladi)
 *
 * Chiqish: { draw_json, draw_width (RASXOD, metrda), draw_png }
 * `draw_width` to'g'ridan-to'g'ri mavjud narx formulasiga tushadi —
 * `products.width` ning o'rniga.
 */
function DrawingModal({ show, onHide, onSave, initial, rollWidthM, rollPriceSom, markup, elementQuantity, quantity, rollRal, rollName, productId, rollProductId, rollCats, needsDrawing, markupTiers, rollSel, onRollSel, onOpenRollPicker,
	// 3 = aksessuar (sukut), 5 = tunikofon — narx formulasi shunga qarab
	// productWidthM — qatordagi joriy kenglik (tunikofonda bo'linish shundan topiladi)
	templateMode, productType, productWidthM, calcMode }) {
	const { t } = useTranslation()

	const VB_W = 820, VB_H = 520

	const [pts, setPts] = useState([])
	const [hem, setHem] = useState({ first: false, last: false, firstDir: 1, lastDir: 1 })
	const [view, setView] = useState('draw')       // draw | tpl | 3d
	const [tool, setTool] = useState('sel')        // sel | draw
	const [sel, setSel] = useState(-1)
	const [bend, setBend] = useState(null)         // 'first' | 'last' | null
	const [colourTop, setColourTop] = useState(true)
	const [gridOn, setGridOn] = useState(true)
	const [preview, setPreview] = useState(null)
	const [yaw, setYaw] = useState(-32)
	const [pitch, setPitch] = useState(20)
	const [zoom, setZoom] = useState(1)
	// 2D yaqinlashtirish / surish — g'ildirak va sudrash bilan
	const [view2, setView2] = useState({ k: 1, tx: 0, ty: 0 })
	const [radial, setRadial] = useState(null)      // uch nuqtasidagi doira menyu
	// RASXODNI QO'LDA YOZISH — avtomat hisob har doim ham to'g'ri kelmaydi
	// (yo'qotishni boshqa joyga «yedirish» kerak bo'ladi). null = avtomat.
	const [consManual, setConsManual] = useState(null)
	/* TUNIKOFON: kesilgan list necha bo'lakka bo'linadi (1 = butun, 2, 3, 4).
	   Doskaning KENGLIGI shundan chiqadi: rulon eni ÷ bo'lak soni.
	   Uzunlik esa chizmaning ёйилмасидан (2026-08-11). */
	const [bolak, setBolak] = useState(1)
	// tunikofon: ko'rinadigan kenglik qo'lda yozilgan bo'lsa (null = avtomat)
	// OMBORDAGI QOLDIQ TASMALAR — shu rulonникi. Chizma sig'sa, undan kesish taklif qilinadi.
	/* NOSTANDART AKSESSUARDA rulon, hajm va soni SHU OYNADA yoziladi —
	   chizmasiz ular ma'nosiz (kenglik chizmadan chiqadi), sotuvchi esa
	   mijoz oldida bitta oynada ishlab, bitta marta yopadi. */
	const [eq, setEq] = useState('')       // hajm — bitta bo'lak uzunligi, m
	const [qty, setQty] = useState('')     // soni
	const [dirty, setDirty] = useState(false)

	/* ⚠️ Bu blok holat (useState) larDAN KEYIN turishi SHART — `rollSel`, `eq`,
	   `qty` shu yerda o'qiladi. Ilgari komponent boshida edi va `const` hali
	   e'lon qilinmagani uchun ReferenceError berib, kassa OQ EKRAN bo'lardi
	   (2026-08-09, foydalanuvchi topdi).
	   Oynada rulon tanlansa — narx, eni, rangi, qalinligi O'SHANIKI;
	   tanlanmasa jadvaldagi qator qiymatlari ishlatiladi. */
	const effRollWidthM = rollSel ? rollSel.length : rollWidthM
	// SOTUV narxi (tannarx emas) — chek qatoridagi hisob bilan bir xil bo'lishi uchun
	const effRollPrice = rollSel ? rollSel.sale_price : rollPriceSom
	const effRollRal = rollSel ? rollSel.ral_code : rollRal
	const effRollName = rollSel ? rollSel.name : rollName
	const effRollPid = rollSel ? rollSel.product_id : rollProductId
	const effEq = needsDrawing ? Number(eq) || 0 : Number(elementQuantity) || 0
	const effQty = needsDrawing ? Number(qty) || 0 : Number(quantity) || 0
	const rollW = Math.round((Number(effRollWidthM) || 1.25) * 1000)   // mm
	// TUNUKA QALINLIGI — rulon nomida yozilgan ("9003/040" → 0.40 mm)
	const thickMM = thicknessFromName(effRollName)
	/* RULON RANGI. Nastroyka → Mahsulotlarda RAL kodi yozilgan bo'lsa o'sha,
	   bo'lmasa shartli rang. Hozircha rulonlarning aksariyatida kod yo'q —
	   shuning uchun 3D da «rang kiritilmagan» deb ogohlantiramiz. */
	const coilHex = ralToHex(effRollRal) || '#96a596'
	const hasRal = !!ralToHex(effRollRal)
	const STEEL_HEX = '#b0b8c4'

	const rollSelRef = useRef(null)   // rulon select'i — Enter uchun fokusdagi qatorni o'qiymiz
	const svgRef = useRef(null)
	const canvasRef = useRef(null)
	const trRef = useRef(null)
	const labelsRef = useRef([])
	const endsRef = useRef([])
	const hemBtnRef = useRef([])
	const dragRef = useRef({ active: false, endGrab: null, drawEnd: 'e' })
	const orbitRef = useRef({ on: false, x: 0, y: 0 })   // 3D aylantirish holati
	const panRef = useRef({ on: false, x: 0, y: 0 })     // 2D ekranni surish
	// ⚠️ Oyna darajasidagi tinglovchi ESKIRGAN holatni ko'radi (closure).
	//    Shuning uchun ko'rsatkich ref da ham saqlanadi.
	const previewRef = useRef(null)
	const clearPreview = () => { previewRef.current = null; setPreview(null) }
	const histRef = useRef([])

	/* ── ochilganda ── */
	useEffect(() => {
		if (!show) return
		const hasPts = !!(initial && initial.pts && initial.pts.length > 1)
		const hasSegs = !!(initial && initial.segments && initial.segments.length)
		if (hasPts) {
			// ANIQ nuqtalar saqlangan — chizma bir zarra ham siljimaydi
			setPts(initial.pts.map(p => ({ x: Number(p.x), y: Number(p.y) })))
			setHem(initial.hem || { first: false, last: false, firstDir: 1, lastDir: 1 })
			setColourTop(initial.colourTop !== false)
		} else if (hasSegs) {
			// eski chizma — faqat yaxlitlangan segmentlar bor
			setPts(ptsFromSegments(initial.segments, initial.dir0))
			setHem(initial.hem || { first: false, last: false, firstDir: 1, lastDir: 1 })
			setColourTop(initial.colourTop !== false)
		} else {
			/* ⚠️ BO'SH VARAQ. Ilgari bu yerda tayyor 100 mm lik chiziq turardi —
			   uni o'chirib bo'lmasdi (delSegAt oxirgi segmentlarni saqlab qolardi)
			   va har yangi chizma o'sha begona chiziqdan boshlanardi (2026-08-11).
			   Endi maydon bo'm-bo'sh, birinchi segmentni usta o'zi tortadi. */
			setPts([])
			setHem({ first: false, last: false, firstDir: 1, lastDir: 1 })
			setColourTop(true)
		}
		setView('draw')
		// bo'sh varaqda darrov qalam turadi — bosdi-yu chiza boshladi
		setTool(hasPts || hasSegs ? 'sel' : 'draw')
		setSel(-1); setBend(null); clearPreview()
		setView2({ k: 1, tx: 0, ty: 0 })          // yaqinlashtirish/surish tiklansin
		setYaw(-32); setPitch(20); setZoom(1)
		setConsManual(initial && initial.consManual ? initial.consManual : null)
		/* Bo'lak soni: avval chizmada saqlangani, bo'lmasa qatordagi kenglikdan
		   hisoblanadi (0.625 → 2), u ham bo'lmasa butun list (1). */
		{
			const w = Number(productWidthM) || 0
			const rw = Number(rollWidthM) || 1.25
			const auto = w > 0 ? Math.round(rw / w) : 1
			setBolak(initial && initial.bolak > 0 ? initial.bolak
				: (auto >= 1 && auto <= 4 ? auto : 1))
		}
		setEq(Number(elementQuantity) > 0 ? String(elementQuantity) : '')
		setQty(Number(quantity) > 0 ? String(quantity) : '')
		setDirty(false)
		histRef.current = []
	}, [show]) // eslint-disable-line react-hooks/exhaustive-deps



	/* Saqlangan segmentlardan chizmani qayta qurish.
	   ⚠️ `dir0` — chizmaning boshlang'ich yo'nalishi. U saqlanmagan paytda
	      (eski chizmalar) -90 dan boshlanadi — o'shanda chizma BURILGAN
	      holatda ochilardi. Yangi chizmalarda haqiqiy yo'nalish yoziladi. */
	function ptsFromSegments(segments, dir0) {
		let dir = (dir0 == null || isNaN(Number(dir0))) ? -90 : Number(dir0)
		let x = 0, y = 0
		const out = [{ x, y }]
		segments.forEach((s, i) => {
			if (i > 0 && s.ang != null) dir += (180 - s.ang) * (s.flip ? -1 : 1)
			const r = dir * Math.PI / 180
			x += s.len * Math.cos(r); y += s.len * Math.sin(r)
			out.push({ x, y })
		})
		return out
	}

	const pushHist = useCallback(() => {
		histRef.current.push(JSON.stringify({ pts, hem }))
		if (histRef.current.length > 60) histRef.current.shift()
		setDirty(true)
	}, [pts, hem])

	const redoRef = useRef([])
	function undo() {
		const s = histRef.current.pop()
		if (!s) return
		redoRef.current.push(JSON.stringify({ pts, hem }))
		const o = JSON.parse(s)
		setPts(o.pts); setHem(o.hem); setSel(-1); setBend(null)
	}
	function redo() {
		const s = redoRef.current.pop()
		if (!s) return
		histRef.current.push(JSON.stringify({ pts, hem }))
		const o = JSON.parse(s)
		setPts(o.pts); setHem(o.hem); setSel(-1); setBend(null)
	}

	/* ═══ KLAVIATURA — oyna ochiq bo'lganda ishlaydi ═══ */
	useEffect(() => {
		if (!show) return
		const onKey = e => {
			const inInput = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || '')
			const mod = e.ctrlKey || e.metaKey
			if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); undo(); return }
			if (mod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) { e.preventDefault(); redo(); return }
			if (mod && e.key.toLowerCase() === 's') { e.preventDefault(); doSave(); return }
			if (inInput) return
			if (e.key === 'Delete' || e.key === 'Backspace') {
				e.preventDefault()
				if (bend && hem[bend]) toggleHem(bend)
				else if (sel >= 0) delSeg(sel)
				return
			}
			if (e.key === 'Escape') {
				// ⚠️ Esc butun oynani YOPMAYDI — faqat tanlovni bekor qiladi.
				// Ilgari react-bootstrap uni ushlab, chizma saqlanmay yo'qolardi.
				e.preventDefault(); e.stopPropagation()
				setSel(-1); setBend(null); setRadial(null); clearPreview()
				return
			}
			if (e.key.toLowerCase() === 'd') { setTool(tl => tl === 'draw' ? 'sel' : 'draw'); return }
			if (e.key === '0') { setView2({ k: 1, tx: 0, ty: 0 }); return }
			if (e.key === '+' || e.key === '=') { setView2(v => ({ ...v, k: Math.min(6, v.k * 1.2) })); return }
			if (e.key === '-') { setView2(v => ({ ...v, k: Math.max(0.3, v.k / 1.2) })); return }
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}) // har renderda yangilanadi — eng oxirgi holat bilan ishlashi uchun

	/* ── hisob ── */
	const segs = segsOf(pts)
	const blank = blankOf(pts)                       // mm
	const calcAuto = consumption(blank, rollW)   // {fits, over, share, remnant, reusable, cons}
	const calc = consManual != null && consManual > 0
		? { ...calcAuto, cons: consManual, manual: true }
		: calcAuto
	const errs = validateDrawing(pts, hem)

	/* ═══ REJIM BAYROQLARI ═══
	   ⚠️ Bular hisob-kitobdan OLDIN turishi SHART — pastdagi `const` lar
	      shularni o'qiydi. Keyinroq e'lon qilinsa ReferenceError bo'lib,
	      butun kassa OQ EKRAN bo'ladi (2026-08-08 da shunday bo'lgan).
	     isTuni  — tur 5 (narx kv.metrda)
	     isDoska — tur 5 + calc_mode 1: chizma UZUNLIKNI beradi, list bo'linadi.
	               Karnizda (2 yoki bo'sh) chizma aksessuardagidek KENGLIKNI beradi. */
	const isTuni = Number(productType) === 5
	const isDoska = isTuni && Number(calcMode ?? 2) === 1

	/* ⚠️ FAQAT DOSKA rejimida hajm — chizmaning ёйилмаси (doska uzunligi).
	   KARNIZDA hajm QO'LDA yoziladi (chizma u yerda kenglikni beradi).
	   Ilgari bu `productType === 5` edi va karnizda ham ёйилма olinardi:
	   usta 100 m yozsa ham «Метр» 0.09 chiqardi (2026-08-11, user topdi). */
	const metr = (isDoska && blank > 0 ? blank / 1000 : effEq) * effQty
	const rollM = rollW > 0 ? metr / (rollW / 1000) * (calc.cons / 1000) : 0
	// Qoldiq tasmaning uzunligi: shuncha metr mahsulot uchun rulon
	// necha metr ochiladi — o'sha uzunlikda qoldiq ham chiqadi.
	/* ⚠️ Sotuvchi ko`pincha chizmani HAJM/SONI yozilishidan OLDIN chizadi.
	   O`shanda metr = 0 bo`lib, qoldiq uzunligi «0.00 m» chiqardi va
	   «Омборга» tanlansa ham hech narsa saqlanmasdi (2026-08-09).
	   Endi uzunlikni CHEK SAQLANGANDA backend hisoblaydi: metr / bo`laklar. */
	const remnantLenM = calc.fits > 0 ? metr / calc.fits : 0
	const lenKnown = metr > 0
	/* USTAMA — mahsulotga jadval biriktirilgan bo'lsa o'shandan (bo'lak soniga
	   qarab), aks holda oddiy `markup`. Chek qatoridagi hisob bilan AYNAN
	   bir xil bo'lishi uchun ikkalasi ham `tierMarkup()` ni chaqiradi. */
	const tier = calc.cons > 0 ? tierMarkup(markupTiers, Math.max(1, Math.floor(rollW / calc.cons + 0.01))) : null
	const markupRoll = tier != null ? tier * rollW / calc.cons : (Number(markup) || 0)
	/* ═══ TUNIKOFON ═══
	   Chizma ёйилмаси — doskaning UZUNLIGI (Ҳажми). Kengligi esa kesilgan
	   listni necha bo'lakka bo'lishdan chiqadi:  kenglik = rulon eni ÷ bo'lak.
	     метр    = ёйилма × Сони
	     рулон м = метр ÷ бўлак
	     кв      = рулон м × рулон эни
	     нарх    = рулон сотув ÷ рулон эни + устама ÷ кенглик   (кв.м га)
	   Jadvaldagi hisob bilan AYNAN bir xil (Cashbox.js:871). */
	const tuniW = isDoska ? (rollW / 1000) / Math.max(1, bolak) : 0   // doska kengligi, m
	const tuniMetr = isDoska ? (blank / 1000) * effQty : 0            // jami doska uzunligi
	const tuniRollM = isDoska ? tuniMetr / Math.max(1, bolak) : 0
	const tuniKv = tuniRollM * (rollW / 1000)

	/* ⚠️ TUNIKOFON narxi KV.METRDA va KENGLIKKA BOG'LIQ EMAS:
	     кв.м нархи = (рулон сотув нархи + устама) ÷ рулон эни
	   Jadvaldagi hisob bilan AYNAN bir xil bo'lishi shart (Cashbox.js:911). */
	const price = rollW <= 0 ? 0
		: isTuni
		? ((Number(effRollPrice) || 0) + (Number(markup) || 0)) / (rollW / 1000)
		: ((Number(effRollPrice) || 0) + markupRoll) * (calc.cons / 1000) / (rollW / 1000)

	/* ── QOLDIQNI BO'LAKKA YEDIRISH ──
	   Rasxod razvyortkadan katta bo'lsa (masalan razvyortka 400, rasxod 416.7),
	   ortiqcha 16.7 mm chiqindiga ketadi. Uni tanlangan bo'lakka qo'shib
	   yuborsak — mahsulot kengroq chiqadi, chiqindi esa deyarli qolmaydi.
	   ⚠️ PASTGA yaxlitlanadi: 61 + 16.7 = 77.67 → 77. Yuqoriga yaxlitlansa
	      razvyortka 417 bo'lib, rulonga 3 marta sig'may qolardi (rasxod 625!). */
	/* ⚠️ BUTUN MILLIMETR QOIDASI — o'zgartirmang.
	   `segsOf()` har bo'lak uzunligini `Math.round` bilan BUTUN mm ga
	   keltiradi (drawingGeometry.js:49), `blankOf()` esa o'sha yaxlitlangan
	   uzunliklar yig'indisi. Ya'ni yoyilma HAR DOIM butun son.

	   Shu sababli 1 mm dan KICHIK qoldiqni qo'shib bo'lmaydi. Urinilsa
	   teskari natija chiqadi:
	     yoyilma 312, sarf 312.5 → bo'lakka +0.5 → 22 → 22.5
	     → Math.round(22.5) = 23 → yoyilma 313
	     → rulonga 4 emas 3 marta sig'adi → sarf 416.7 (+33%!)

	   2026-08-12: men buni «0.1 mm aniqlik» bilan tuzatmoqchi bo'ldim va
	   AYNAN shu xatoni keltirib chiqardim (foydalanuvchi topdi). Asl
	   `Math.floor` + `< 1` sharti TO'G'RI edi — u shu holatni ataylab
	   rad etadi. Qaytarildi. */
	const leftover = Math.floor(calc.cons - blank)
	function feedLeftover(i) {
		if (leftover < 1 || !segs[i]) return
		pushHist()
		setPts(setSegLen(pts, i, Math.floor(segs[i].len + calc.cons - blank)))
		// ⚠️ bu yerda setSel() QILINMAYDI — u o'ng paneldagi bo'laklar ro'yxatini
		//    bitta bo'lak oynasiga almashtirib yuborardi.
	}

	/* ── CHIZMANI BURISH / AKS ETTIRISH ──
	   Ikkalasi ham razvyortkaga TEGMAYDI — uzunlik va burchaklar o'zgarmaydi,
	   demak narx ham, ombor ham o'zgarmaydi. Faqat detal qanday yotishi o'zgaradi. */
	function rotate(deg) { pushHist(); setPts(rotatePts(pts, deg)) }
	function mirror() {
		pushHist()
		setPts(mirrorPts(pts))
		// qayirma uchlari ham aks etdi — bayrog'i ham teskarisiga o'girilsin
		setHem(h => ({ ...h, firstDir: -(h.firstDir || 1), lastDir: -(h.lastDir || 1) }))
	}
	const zoom2 = f => setView2(v => ({ ...v, k: Math.max(0.3, Math.min(6, v.k * f)) }))

	/* ── BUTUN CHIZMANI TOZALASH ──
	   Segmentlarni ✕ bilan bittalab o'chirish OXIRGISIDA to'xtaydi (bitta
	   nuqtali chizma ma'nosiz). Noldan boshlash uchun shu tugma.
	   Ctrl+Z bilan qaytariladi — shuning uchun tasdiq so'ralmaydi. */
	function clearAll() {
		if (pts.length < 2) return
		pushHist()
		setPts([])
		setHem({ first: false, last: false, firstDir: 1, lastDir: 1 })
		setSel(-1); setBend(null); setRadial(null); clearPreview()
		setConsManual(null)
		setTool('draw')
	}

	/* Segment o'chirish — HAMMA joydan shu funksiya chaqiriladi.
	   ⚠️ Oxirgi segment o'chirilsa butun chizma tozalanadi. Ilgari ✕ tugmasi
	      shunchaki o'lik (disabled) turardi va foydalanuvchi ekrandagi yagona
	      chiziqni hech qanday yo'l bilan yo'qota olmasdi (2026-08-11). */
	function delSeg(i) {
		if (segs.length <= 1) { clearAll(); return }
		pushHist()
		const r = delSegAt(pts, hem, i)
		setPts(r.pts); setHem(r.hem); setSel(-1)
	}

	/* ── ko'rish transformatsiyasi ──
	   ⚠️ Nuqta yo'q bo'lsa (bo'sh varaq) sun'iy 400 mm lik ramka olinadi.
	      Bo'lmasa `Math.min(...[])` = Infinity bo'lib butun transformatsiya
	      NaN chiqadi va maydonga bosib ham bo'lmaydi. */
	function fit(points, pad, manual) {
		/* ⚠️ BO'SH VARAQ masshtabi. Ilgari ±200 (400 mm) edi — 1 mm atigi 0.8
		   pikselga to'g'ri kelardi va qo'lning tabiiy harakati darrov ~10 SM
		   berardi; 1 sm chizish uchun 8 piksel siljitish kerak edi, ya'ni
		   amalda imkonsiz (2026-08-11, foydalanuvchi shikoyati).
		   ±75 (150 mm) da 1 mm ≈ 2.2 piksel: 1 sm ≈ 22 piksel — qulay.
		   Uzun segment kerak bo'lsa taxminan chizib, o'ng paneldagi maydonga
		   aniq raqam yoziladi; birinchi segmentdan keyin ko'rinish o'zi moslashadi. */
		const src = points.length ? points : [{ x: -75, y: -75 }, { x: 75, y: 75 }]
		const xs = src.map(p => p.x), ys = src.map(p => p.y)
		const minX = Math.min(...xs), maxX = Math.max(...xs)
		const minY = Math.min(...ys), maxY = Math.max(...ys)
		const sw = Math.max(maxX - minX, 40), sh = Math.max(maxY - minY, 40)
		let k = Math.min((VB_W - pad * 2) / sw, (VB_H - pad * 2) / sh)
		let ox = pad + (VB_W - pad * 2 - sw * k) / 2, oy = pad + (VB_H - pad * 2 - sh * k) / 2
		if (manual) {
			// qo'lda yaqinlashtirish/surish — avtomat moslashning USTIGA qo'yiladi
			ox = VB_W / 2 + (ox - VB_W / 2) * view2.k + view2.tx
			oy = VB_H / 2 + (oy - VB_H / 2) * view2.k + view2.ty
			k *= view2.k
		}
		const f = p => ({ x: ox + (p.x - minX) * k, y: oy + (p.y - minY) * k })
		f.inv = p => ({ x: (p.x - ox) / k + minX, y: (p.y - oy) / k + minY })
		f.k = k
		return f
	}

	function toVB(e) {
		const svg = svgRef.current
		if (!svg) return { x: 0, y: 0 }
		const p = svg.createSVGPoint()
		p.x = e.clientX; p.y = e.clientY
		return p.matrixTransform(svg.getScreenCTM().inverse())
	}

	/* ── yopishtirish ── */
	function computeSnap(mm) {
		const de = dragRef.current.drawEnd
		let anchor, base
		if (pts.length < 2) {
			/* BO'SH VARAQ — birinchi segment bosilgan nuqtadan chiziladi.
			   Tayanch yo'nalish yo'q, shuning uchun burchak mutlaq o'lchanadi. */
			anchor = dragRef.current.origin || pts[0] || { x: 0, y: 0 }
			base = 0
		} else if (de === 'e') {
			const last = pts[pts.length - 1], prev = pts[pts.length - 2] || last
			anchor = last; base = Math.atan2(last.y - prev.y, last.x - prev.x) * 180 / Math.PI
		} else {
			const first = pts[0], nxt = pts[1] || first
			anchor = first; base = Math.atan2(first.y - nxt.y, first.x - nxt.x) * 180 / Math.PI
		}
		const dx = mm.x - anchor.x, dy = mm.y - anchor.y
		const len = Math.max(10, snapTo(Math.hypot(dx, dy), 10))
		// YO'NALISH MUTLAQ (katakka nisbatan) yopishadi — tik tik, yotiq yotiq
		const absDir = snapTo(Math.atan2(dy, dx) * 180 / Math.PI, 5)
		const rel = ((absDir - base) + 540) % 360 - 180
		const ang = Math.round(180 - Math.abs(rel))
		const onAxis = Math.abs(((absDir % 45) + 45) % 45) < 0.5
		const r = absDir * Math.PI / 180
		return {
			pt: { x: anchor.x + len * Math.cos(r), y: anchor.y + len * Math.sin(r) },
			len, ang, snapped: onAxis || [45, 90, 135, 180].some(v => Math.abs(ang - v) < 0.5),
		}
	}

	/* ── 3D ── */
	useEffect(() => {
		if (view !== '3d' || !canvasRef.current) return
		render3d(canvasRef.current, pts, hem, colourTop, yaw, pitch, zoom,
			(effEq || 3) * 1000, effRollRal, thickMM)
	}, [view, pts, hem, colourTop, yaw, pitch, zoom, effEq, effRollRal, thickMM])

	/* ── saqlash ── */
	function buildPayload() {
		return {
			draw_json: {
				segments: segs.map((s, i) => ({ len: s.len, ang: s.ang, flip: s.flip })),
				hem,
				colourTop,
				blank,
				consManual,
				bolak,          // tunikofon: list necha bo'lakka bo'lingan
				dir0: dir0Of(pts),          // yo'nalish — qayta ochganda burilib qolmasin
				// ⚠️ ANIQ nuqtalar. segsOf() uzunlik va burchakni butun songa yaxlitlaydi;
				//    faqat shundan qayta qurilsa, xato segmentdan segmentga yig'ilib
				//    8 segmentli chizmani 18 mm gacha qiyshaytirardi.
				pts: pts.map(p => ({ x: Math.round(p.x * 1e4) / 1e4, y: Math.round(p.y * 1e4) / 1e4 })),
			},
			/* ⚠️ BIR XIL NOM, IKKI XIL MA'NO — turga qarab:
			     aksessuar (3): SARF — rulondan ketadigan tasma eni
			     tunikofon (5): DOSKA KENGLIGI — rulon eni ÷ bo'lak soni
			   Ikkalasida ham `effWidth` sifatida ishlatiladi va rulon
			   sarfini belgilaydi, shuning uchun bitta maydon yetadi. */
			draw_width: isDoska ? tuniW : calc.cons / 1000,
			draw_png: svgToDataUri(),
			blank_mm: blank,
			cons_mm: calc.cons,
			// oynada tanlangan rulon / hajm / soni — qatorga ko'chiriladi
			roll: needsDrawing ? rollSel : null,
			element_quantity: needsDrawing && eq !== '' ? Number(eq) : null,
			quantity: needsDrawing && qty !== '' ? Number(qty) : null,
		}
	}
	/* CHEKKA KETADIGAN CHIZMA.

	   ⚠️ 2026-08-13 gacha bu yerda faqat YALANG'OCH KONTUR eksport qilinardi:
	      o'lchamlar, burchaklar va rang tomoni ekranda bor edi, chekда esa
	      YO'Q. Usta shaklni ko'rardi-yu, necha millimetrdan bukishni bilmasdi.

	   Endi `drawingDataUri()` — ekran bilan BIR XIL geometriya yordamchilarini
	   ishlatadigan umumiy chizuvchi (`drawingGeometry.js`). Shu sababli ikkisi
	   kelajakda ham ajralib ketmaydi. */
	function svgToDataUri() {
		try {
			return drawingDataUri({
				pts, hem, colourTop, coilHex,
				width: VB_W, height: VB_H,
				forPrint: true,
			})
		} catch (e) { return null }
	}

	/* Yopishdan oldin — saqlanmagan chizma bekorga yo'qolmasin */
	function askClose() {
		if (dirty && blank > 0) {
			if (!window.confirm(t('unsaved_drawing_confirm'))) return
		}
		onHide()
	}

	/* ⚠️ Ilgari saqlashda «shablonga qo'shamizmi?» deb so'ralardi va shablon
	   CHEK ichidan yaratilardi — teskari tartib edi (2026-08-09).
	   Endi standart chizma MAHSULOT KARTOCHKASIDA bir marta chiziladi,
	   sotuvda esa nom tanlanishi bilan o'zi chiqadi. */
	function doSave() {
		if (pts.length < 2) { toast.error(t('draw_empty')); return }
		if (calc.over) { toast.error(t('draw_too_wide')); return }
		if (errs.length) { toast.error(errs[0].msg); return }
		/* ⚠️ RULON MAJBURIY (chek rejimida). Rulonsiz saqlansa narx bazasi
		   (rulon sotuv narxi) bo'sh qolib, qator 0 so'mga tushib ketardi va
		   ombordan ham hech narsa ayirilmasdi (2026-08-11, user talabi). */
		if (!templateMode && needsDrawing && !rollSel && !rollProductId) {
			toast.error(t('select_roll_first')); return
		}
		onSave(buildPayload())
	}

	/* ═════════ chizish ═════════ */
	/* ⚠️ Bo'sh varaqda ham transformatsiya BO'LISHI SHART — `onDown` uni
	   tekshirib turadi, `null` bo'lsa maydon umuman bosilmaydi. */
	const trans = fit(pts.length > 1 ? pts : [], 72, true)
	trRef.current = trans

	function renderCanvas() {
		if (!trans) return null

		/* ── BO'SH VARAQ ──
		   Hech qanday chiziq yo'q. Faqat sudralayotgan birinchi segment
		   ko'rsatiladi. Eski hit-boxlar ham tozalanadi — bo'lmasa o'chirilgan
		   chizmaning yorliqlari joyida qolib, bo'sh maydonda bosilib ketardi. */
		if (pts.length < 2) {
			labelsRef.current = []; endsRef.current = []; hemBtnRef.current = []
			if (!preview) return null
			const a = trans(dragRef.current.origin || { x: 0, y: 0 })
			const b = trans(preview.pt)
			const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2
			return (
				<Fragment key="pv0">
					<line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="var(--dw-ink, #1c1830)"
						strokeWidth="2.8" strokeLinecap="round" />
					<circle cx={a.x} cy={a.y} r="5" fill="var(--dw-pt, #a8c8f2)" stroke="var(--dw-pt-bd, #1b4f9e)" strokeWidth="1.4" />
					<rect x={mx - 19} y={my - 9} width="38" height="18" rx="3" fill="var(--dw-lbl-bg, #fff)" stroke="var(--dw-lbl-bd, #c9c4d6)" />
					<text x={mx} y={my + 4} textAnchor="middle" fontSize="11.5"
						fill="#2f7ae5" fontWeight="600">{preview.len}</text>
				</Fragment>
			)
		}

		const P = pts.map(trans)
		const S = segs
		const labels = []
		const ends = []
		const hemBtns = []
		const out = []

		if (sel >= 0 && sel < P.length - 1) {
			out.push(<line key="sel" x1={P[sel].x} y1={P[sel].y} x2={P[sel + 1].x} y2={P[sel + 1].y}
				stroke="#461BA1" strokeWidth="8" strokeLinecap="round" opacity=".25" />)
		}

		// ═══ BITTA chiziq (nuIT dagidek). Ilgari qora + qalin yashil ikkita
		//     chiziq edi — foydalanuvchi «1 ta yetadi» dedi (2026-08-08).
		//     Rang tomoni chiziqning O'Z rangi bilan bildiriladi.
		/* ── RANG TOMONI STRELKALARI (nuIT dagidek) ──
		   Chiziqning qaysi tomoni bo'yalganini strelka ko'rsatadi. Strelka
		   rulonning O'Z rangida bo'ladi — oq bo'yoqda ham ko'rinsin deb
		   atrofi to'q rang bilan chizilgan. Eng uzun 3 segmentga qo'yiladi. */
		const cSign = colourTop ? 1 : -1
		S.map((s, i) => ({ i, len: s.len })).sort((a, b) => b.len - a.len).slice(0, 3)
			.forEach(({ i }) => {
				const a = P[i], b = P[i + 1]
				const dx = b.x - a.x, dy = b.y - a.y, LL = Math.hypot(dx, dy) || 1
				if (LL < 30) return
				const nx = -dy / LL * cSign, ny = dx / LL * cSign      // bo'yalgan tomon normali
				// ⚠️ 0.5 EMAS, 0.32 — o'lcham yorlig'i segment o'rtasida turadi,
				//    strelka o'sha yerga qo'yilsa ustma-ust tushib ketardi.
				const mx = a.x + dx * 0.32, my = a.y + dy * 0.32
				const at = d => ({ x: mx + nx * d, y: my + ny * d })
				const tip = at(3), base = at(14), tail = at(34)
				const px = -(-ny) * 5.2, py = (-nx) * 5.2               // uch qanotlari
				const head = `M${tip.x},${tip.y} L${base.x + px},${base.y + py} L${base.x - px},${base.y - py} Z`
				// oq halqa — qora chiziq ustida ham, katak ustida ham ko'rinsin
				out.push(<line key={'cw' + i} x1={tail.x} y1={tail.y} x2={base.x} y2={base.y}
					stroke="#fff" strokeWidth="6.5" strokeLinecap="round" />)
				out.push(<path key={'cq' + i} d={head} fill="#fff" stroke="#fff" strokeWidth="3.4" strokeLinejoin="round" />)
				out.push(<line key={'cs' + i} x1={tail.x} y1={tail.y} x2={base.x} y2={base.y}
					stroke={coilHex} strokeWidth="3" strokeLinecap="round" />)
				out.push(<path key={'ch' + i} d={head}
					fill={coilHex} stroke="var(--dw-ink, #2b2438)" strokeWidth="1" strokeLinejoin="round" />)
			})

		out.push(<path key="main" d={pathWithHems(P, pts, hem, trans.k)} fill="none"
			stroke="var(--dw-ink, #1c1830)"
			strokeWidth="2.8" strokeLinejoin="round" strokeLinecap="round" />)

		/* ── YORLIQLAR ──
		   Avval hammasining «xohlagan» joyini yig'amiz, keyin bitta joyda
		   to'qnashuvni yechamiz. Ilgari har biri o'z joyiga qo'yilardi va
		   raqamlar bir-birining ustiga chiqib ketardi (2026-08-08). */
		const wanted = []
		const arcs = []

		S.forEach((sg, i) => {
			if (sg.ang == null) return
			const p = P[i - 1], v = P[i], n = P[i + 1], R = 30
			const a1 = Math.atan2(p.y - v.y, p.x - v.x)
			let d = Math.atan2(n.y - v.y, n.x - v.x) - a1
			while (d <= -Math.PI) d += 2 * Math.PI
			while (d > Math.PI) d -= 2 * Math.PI
			const x1 = v.x + R * Math.cos(a1), y1 = v.y + R * Math.sin(a1)
			const x2 = v.x + R * Math.cos(a1 + d), y2 = v.y + R * Math.sin(a1 + d)
			const mid = a1 + d / 2
			arcs.push(<path key={'arc' + i}
				d={`M${x1.toFixed(1)},${y1.toFixed(1)} A${R},${R} 0 0 ${d > 0 ? 1 : 0} ${x2.toFixed(1)},${y2.toFixed(1)}`}
				fill="none" stroke="#e08a92" strokeWidth="1.5" />)
			wanted.push({
				kind: 'ang', i, w: 38, h: 18,
				ax: v.x, ay: v.y, nx: Math.cos(mid), ny: Math.sin(mid), off: R + 17,
				// USTAGA BUKILISH ko'rsatiladi: ichki 135° → 45°
				text: toBend(sg.ang) + '°',
			})
		})

		S.forEach((sg, i) => {
			const a = P[i], b = P[i + 1]
			const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2
			const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy) || 1
			wanted.push({
				kind: 'len', i, w: 40, h: 19,
				ax: mx, ay: my, nx: -dy / L, ny: dx / L, off: 24,
				text: String(sg.len), ux: dx / L, uy: dy / L,
			})
		})

		out.push(<Fragment key="arcs">{arcs}</Fragment>)

		for (const L of placeLabels(wanted, P)) {
			labels.push({ kind: L.kind, i: L.i, x: L.x, y: L.y, w: L.w, h: L.h })
			const isAng = L.kind === 'ang'
			// yorliqni chiziqqa bog'lovchi ingichka chiziq — uzoqlashganda yo'qolmasin
			out.push(<Fragment key={L.kind + L.i}>
				<line x1={L.ax} y1={L.ay} x2={L.x} y2={L.y}
					stroke={isAng ? '#f0cfd2' : '#cfcada'} strokeWidth="1" strokeDasharray="3 3" />
				<rect x={L.x - L.w / 2} y={L.y - L.h / 2} width={L.w} height={L.h} rx="3" fill="#fff"
					stroke={isAng ? '#e8b6ba' : '#a89fc4'} style={{ cursor: 'pointer' }} />
				<text x={L.x} y={L.y + 4} textAnchor="middle" fontSize={isAng ? 11 : 11.5}
					fill={isAng ? '#d1454f' : '#1c1830'} fontWeight={isAng ? 600 : 400}
					style={{ cursor: 'pointer' }}>{L.text}</text>
			</Fragment>)
		}

		// qayirma belgisi
		const hv = []
		if (hem.first && P.length > 2) hv.push(1)
		if (hem.last && P.length > 2) hv.push(P.length - 2)
		hv.forEach(i => {
			const V = P[i], A0 = P[i - 1]
			const ux = V.x - A0.x, uy = V.y - A0.y, L = Math.hypot(ux, uy) || 1
			const bx = V.x + ux / L * 16, by = V.y + uy / L * 16
			hemBtns.push({ which: i === 1 ? 'first' : 'last', x: bx, y: by })
			out.push(<Fragment key={'h' + i}>
				<rect x={bx - 15} y={by - 9} width="30" height="18" rx="9" fill="#a8d24a" stroke="#6d9c1e"
					strokeWidth="1.2" style={{ cursor: 'pointer' }} />
				<path d={`M${bx - 7},${by + 2} L${bx + 4},${by + 2} A3.2,3.2 0 0 0 ${bx + 4},${by - 4} L${bx - 3},${by - 4}`}
					fill="none" stroke="#2b3d0c" strokeWidth="1.7" strokeLinecap="round" />
			</Fragment>)
		})

		// ikki uch
		const A = P[0], Z = P[P.length - 1], r = 8
		ends.push({ which: 'first', x: A.x, y: A.y }, { which: 'last', x: Z.x, y: Z.y })
		out.push(<rect key="e0" x={A.x - r} y={A.y - r} width={r * 2} height={r * 2} rx="2"
			fill="var(--dw-pt, #a8c8f2)" stroke="var(--dw-pt-bd, #1b4f9e)" strokeWidth="1.4" style={{ cursor: 'pointer' }} />)
		out.push(<path key="e1" d={`M${Z.x - r},${Z.y - r} L${Z.x + r},${Z.y} L${Z.x - r},${Z.y + r} Z`}
			fill="#f6c68a" stroke="#9a5c04" strokeWidth="1.4" style={{ cursor: 'pointer' }} />)

		// sudrab chizish ko'rsatkichi
		if (preview) {
			const anchor = dragRef.current.drawEnd === 'e' ? P[P.length - 1] : P[0]
			const tip = trans(preview.pt)
			const mx = (anchor.x + tip.x) / 2, my = (anchor.y + tip.y) / 2
			out.push(<Fragment key="pv">
				<line x1={anchor.x} y1={anchor.y} x2={tip.x} y2={tip.y} stroke="#b9b3c9" strokeWidth="1.3" />
				<rect x={mx - 19} y={my - 9} width="38" height="18" rx="3" fill="var(--dw-lbl-bg, #fff)" stroke="var(--dw-lbl-bd, #c9c4d6)" />
				<text x={mx} y={my + 4} textAnchor="middle" fontSize="11.5" fill="#2f7ae5" fontWeight="600">{preview.len}</text>
				<circle cx={anchor.x} cy={anchor.y} r="15" fill={preview.snapped ? '#1f7a54' : '#2b2438'}
					stroke={preview.snapped ? '#8ec63f' : '#d6d2e0'} strokeWidth="3" />
				<text x={anchor.x} y={anchor.y + 4} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{preview.ang}</text>
			</Fragment>)
		}

		labelsRef.current = labels
		endsRef.current = ends
		hemBtnRef.current = hemBtns
		return out
	}

	/* ═════════ sichqoncha ═════════ */
	function onDown(e) {
		if (view !== 'draw' || !trans) return

		/* ── EKRANNI SURISH — G'ILDIRAK TUGMASI (yoki Shift + chap) ──
		   ⚠️ HAR QANDAY asbobda ishlashi SHART. Ilgari bu yerda shunchaki
		      `return` turardi va surish faqat «▣ tanlash» asbobida, bo'sh joyni
		      chap tugma bilan sudrab qilinardi. Qalam yoqilgan bo'lsa chap tugma
		      chizib yuborar, g'ildirak esa umuman ishlamas edi — chizmani surish
		      imkonsiz bo'lib qolardi (2026-08-11, foydalanuvchi topdi).
		   ⚠️ preventDefault() — Chrome ning g'ildirakli «avto-siljish» rejimi
		      (dumaloq ko'rsatkich) yonib ketmasligi uchun. */
		if (e.button === 1 || e.shiftKey) {
			e.preventDefault()
			panRef.current = { on: true, x: e.clientX, y: e.clientY }
			dragRef.current = { ...dragRef.current, active: false, endGrab: null, origin: null }
			bindWindow()
			clearPreview()
			return
		}

		const v = toVB(e)

		const hb = hemBtnRef.current.find(B => Math.abs(v.x - B.x) < 18 && Math.abs(v.y - B.y) < 12)
		if (hb) { e.preventDefault(); setBend(hb.which); return }

		/* ⚠️ 16 emas, 20 — chizish endi ASOSAN shu ikki belgidan boshlanadi
		   (chap tugma bo'sh joyda suradi), demak ularni ushlash oson bo'lsin. */
		const end = endsRef.current.find(E => Math.hypot(v.x - E.x, v.y - E.y) < 20)
		if (end) {
			e.preventDefault()
			dragRef.current = { active: false, endGrab: { which: end.which, x0: e.clientX, y0: e.clientY, moved: false, vx: end.x, vy: end.y }, drawEnd: end.which === 'first' ? 's' : 'e' }
			bindWindow()
			return
		}

		const hit = labelsRef.current.find(L => Math.abs(v.x - L.x) < L.w / 2 + 5 && Math.abs(v.y - L.y) < L.h / 2 + 5)
		if (hit) { e.preventDefault(); openInline(hit); return }

		if (tool === 'draw') {
			const mm0 = trans.inv({ x: v.x, y: v.y })
			dragRef.current = {
				active: true, endGrab: null,
				drawEnd: pts.length > 1 ? (dragRef.current.drawEnd || 'e') : 'e',
				// bo'sh varaqda birinchi nuqta — bosilgan joyning o'zi (katakka yopishtirilgan)
				origin: pts.length > 1 ? null : { x: snapTo(mm0.x, 10), y: snapTo(mm0.y, 10) },
			}
			bindWindow()
			{ const pv = computeSnap(mm0); previewRef.current = pv; setPreview(pv) }
			return
		}

		// segment tanlash
		const P = pts.map(trans)
		let best = -1, bd = 12
		for (let i = 0; i < P.length - 1; i++) {
			const a = P[i], b = P[i + 1], dx = b.x - a.x, dy = b.y - a.y, L2 = dx * dx + dy * dy || 1
			const tt = Math.max(0, Math.min(1, ((v.x - a.x) * dx + (v.y - a.y) * dy) / L2))
			const d = Math.hypot(v.x - (a.x + dx * tt), v.y - (a.y + dy * tt))
			if (d < bd) { bd = d; best = i }
		}
		if (best >= 0) { setSel(best); setBend(null); setRadial(null); return }

		// bo'sh joyda sudrash = EKRANNI SURISH (nuIT dagidek)
		panRef.current = { on: true, x: e.clientX, y: e.clientY }
		bindWindow()
		setSel(-1); setBend(null); setRadial(null)
	}

	/* ⚠️ setPointerCapture ga tayanmaymiz — u ba'zi holatlarda ishlamaydi
	   (sichqoncha maydondan chiqib ketsa sudrash «osilib» qolardi).
	   Buning o'rniga OYNA darajasida tinglaymiz — ishonchli. */
	function bindWindow() {
		const move = ev => onMove(ev)
		const up = ev => { onUp(ev); window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up) }
		window.addEventListener('pointermove', move)
		window.addEventListener('pointerup', up)
	}

	function onMove(e) {
		if (!trans) return
		// ekranni surish
		const pn = panRef.current
		if (pn.on) {
			const dx = e.clientX - pn.x, dy = e.clientY - pn.y
			panRef.current = { on: true, x: e.clientX, y: e.clientY }
			const r = svgRef.current.getBoundingClientRect()
			const kx = VB_W / r.width, ky = VB_H / r.height
			setView2(v => ({ ...v, tx: v.tx + dx * kx, ty: v.ty + dy * ky }))
			return
		}
		const g = dragRef.current
		if (g.endGrab && !g.active) {
			if (Math.hypot(e.clientX - g.endGrab.x0, e.clientY - g.endGrab.y0) < 5) return
			g.endGrab.moved = true; g.active = true
		}
		if (!g.active) return
		const v = toVB(e)
		{ const pv = computeSnap(trans.inv({ x: v.x, y: v.y })); previewRef.current = pv; setPreview(pv) }
	}

	function onUp() {
		if (panRef.current.on) { panRef.current = { on: false, x: 0, y: 0 } }
		const g = dragRef.current
		if (g.endGrab && !g.endGrab.moved) {
			// SUDRAMASDAN bosildi → uchidagi DOIRA MENYU (nuIT dagidek)
			const w = g.endGrab.which
			if (hem[w]) setBend(w)
			else setRadial({ which: w, x: g.endGrab.vx, y: g.endGrab.vy })
			dragRef.current = { active: false, endGrab: null, drawEnd: g.drawEnd }
			return
		}
		if (!g.active) { dragRef.current = { ...g, endGrab: null }; return }
		const pv = previewRef.current
		if (pv && pv.len >= 10) {
			pushHist()
			const fresh = pts.length < 2          // bo'sh varaqdagi BIRINCHI segment
			// funksional yangilash — eskirgan closure dan himoya
			setPts(prev => prev.length < 2
				// bo'sh varaq: bitta harakatda IKKI nuqta tushadi (boshi va uchi)
				? [g.origin || { x: 0, y: 0 }, pv.pt]
				: (g.drawEnd === 'e' ? [...prev, pv.pt] : [pv.pt, ...prev]))
			setHem(h => g.drawEnd === 'e' ? { ...h, last: false } : { ...h, first: false })
			/* ⚠️ Birinchi segment tushishi bilan qalam O'CHADI.
			   Foydalanuvchi talabi (2026-08-11): «bossam chizvoryabdi — 2 tomondagi
			   strelkani bosib chozmagunimcha chizmasin». Ya'ni chizma bir marta
			   paydo bo'lgach chap tugma SURADI/TANLAYDI, davomi esa faqat ikki
			   uchidagi belgidan tortilganda chiziladi. Qalam kerak bo'lsa —
			   chap ustundagi ✎ tugmasi bilan qo'lda yoqiladi. */
			if (fresh) setTool('sel')
		}
		dragRef.current = { active: false, endGrab: null, drawEnd: g.drawEnd, origin: null }
		clearPreview()
	}

	/* ── ekranda raqamni tahrirlash ── */
	const [inline, setInline] = useState(null)
	function openInline(L) {
		const svg = svgRef.current
		const p = svg.createSVGPoint(); p.x = L.x; p.y = L.y
		const sc = p.matrixTransform(svg.getScreenCTM())
		const box = svg.parentElement.getBoundingClientRect()
		const S = segsOf(pts)
		setInline({
			...L,
			left: sc.x - box.left - 46,
			top: sc.y - box.top - 16,
			value: L.kind === 'ang' ? toBend(S[L.i].ang) : S[L.i].len,
		})
	}
	function applyInline(v) {
		if (!inline) return
		const n = Number(v)
		if (n > 0) {
			pushHist()
			setPts(inline.kind === 'ang' ? setSegAng(pts, inline.i, fromBend(n))
				: setSegLen(pts, inline.i, Math.max(1, n)))
		}
		setInline(null)
	}

	/* ── qayirma ── */
	function toggleHem(which) {
		if (pts.length < 3) return
		pushHist()
		if (hem[which]) {
			setPts(which === 'first' ? pts.slice(1) : pts.slice(0, -1))
			setHem({ ...hem, [which]: false })
			setBend(null)
		} else {
			const p = hemPointFor(pts, which, hem[which + 'Dir'] || 1)
			setPts(which === 'first' ? [p, ...pts] : [...pts, p])
			setHem({ ...hem, [which]: true })
		}
	}

	if (!show) return null

	/* ⚠️ QORONG'I MAVZU — INLINE USLUB CSS BILAN YENGILMAYDI.
	   Rulon selektining rangi react-select'ning `styles` proplarida qattiq
	   yozilgan edi (`#fff` / `#fff5f5`), shuning uchun qorong'i mavzuda oyna
	   o'rtasida OQ chiziq bo'lib turardi va qizil placeholder to'q fonda
	   deyarli o'qilmasdi (foydalanuvchi surati, 2026-08-13).
	   Mavzu `<html data-theme="dark">` bilan aniq belgilanadi (ThemeToggle.js:30),
	   OS sozlamasiga ergashmaydi — shuning uchun shu yerda o'qish ishonchli. */
	const isDark = document.documentElement.getAttribute('data-theme') === 'dark'
	const selColors = {
		okBg:     isDark ? '#243356' : '#fff',
		errBg:    isDark ? '#3a2431' : '#fff5f5',
		okBorder: isDark ? '#33436b' : '#ced4da',
		errText:  isDark ? '#ff9b9b' : '#dc3545',
		text:     isDark ? '#e6ecf7' : undefined,
	}

	return (
		<Modal show={show} onHide={askClose} dialogClassName="modal-fullscreen-lg-down" size="xl"
			backdrop="static" keyboard={false}>
			<Modal.Header closeButton>
				<Modal.Title style={{ fontSize: '17px' }}>
					{t('drawing')}
					<span className="text-muted ms-2" style={{ fontSize: '13px' }}>
						{templateMode
							? <>{t('stretch_out')}: <b>{blank}</b> {t('mm')}</>
							: isDoska
						/* Doskada «Сарф» ma'nosiz — ёйилма uzunlik, rulon eniga
						   sig'ish masalasi yo'q (2026-08-11) */
						? <>{t('roll')}: {rollW} {t('mm')}</>
						: <>{t('roll')}: {rollW} {t('mm')} · {t('consumption')}: <b>{calc.cons.toFixed(1)}</b> {t('mm')}</>}
						{thickMM ? <> · {t('thickness')}: <b>{thickMM.toFixed(2)}</b> {t('mm')}</> : null}
					</span>
				</Modal.Title>
			</Modal.Header>

			<Modal.Body className="p-0">
				{/* rejim tablari */}
				<div className="d-flex align-items-center border-bottom px-3">
					{/* 3D endi tab emas — pastdagi dumaloq tugmadan ochiladi */}
					{/* Faqat «Чизма» — shablonlar tabi olib tashlandi (2026-08-09).
					    3D o'sha tabning ichki ko'rinishi, pastdagi dumaloq tugmadan ochiladi. */}
					<span className="text-primary fw-bold px-3 py-2" style={{ fontSize: '13px' }}>
						{t('drawing')}
					</span>
					{/* «Rang tomonini almashtirish» endi chap asboblar ustunida — nuIT dagidek */}
				</div>

				{/* ── NOSTANDART: RULON · HAJM · SONI shu yerda ──
				    Standart mahsulotda bu maydonlar jadvalda qoladi. Nostandartda
				    esa ular chizmasiz ma'nosiz — kenglik chizmadan chiqadi, qoldiq
				    tasmaning uzunligi ham hajmga bog'liq. */}
				{needsDrawing && !templateMode &&
					<div className="d-flex align-items-center gap-2 flex-wrap border-bottom px-3 py-2 bg-white"
						style={{ fontSize: '13px' }}>
						<span className="text-muted">{t('roll')}:</span>
						{/* Jadvaldagi rulon tanlash bilan BIR XIL: qidiruvli ro'yxat +
						    «⋯» tugmasi partiya jadvalini ochadi (qoldiq, sana, ta'minotchi). */}
						{/* ⚠️ ENTER ni react-select'DAN OLDIN ushlaymiz (capture fazasi).
						    Aks holda Enter fokusdagi qatorni «tanlab» ro'yxatni yopar,
						    lekin partiya hali tanlanmagani uchun maydon BO'SHAB qolardi
						    (2026-08-09). Jadvaldagi handleRollCategoryKeyDown bilan bir xil. */}
						<div className="position-relative" style={{ width: '340px' }}
							onKeyDownCapture={e => {
								if (e.key !== 'Enter') return
								e.preventDefault(); e.stopPropagation()
								const foc = rollSelRef.current?.state?.focusedOption
								onOpenRollPicker && onOpenRollPicker(foc || null)
							}}>
							{/* JADVALDAGI BILAN AYNAN BIR XIL: ro'yxatda rulon NOMI bitta
							    qator bo'lib turadi, qavsda nechta partiyasi borligi. Partiyani
							    tanlash uchun ichiga kiriladi («⋯» yoki nomni tanlash) —
							    o'shanda partiya jadvali ochiladi. */}
							{/* ⚠️ Rulon tanlanmaganda maydon KO'RINMAY qolardi — chegara
							    ham, yozuv ham yo'q edi, usta qayerni bosishni bilmasdi
							    (2026-08-11). Endi chegara va «Рулонни танланг» yozuvi bor,
							    tanlanmagani qizil, tanlangani oddiy. */}
							<Select
								ref={rollSelRef}
								classNamePrefix="border-none bs-none"
								options={rollCats || []}
								value={rollSel ? { name: rollSel.name } : ''}
								onChange={option => onOpenRollPicker && onOpenRollPicker(option)}
								placeholder={t('select_roll_first')}
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={o => `${o.name}${o.products?.length ? ` (${o.products.length})` : ''}`}
								getOptionValue={o => o.id ?? o.name}
								menuPortalTarget={document.body}
								styles={{
									menuPortal: base => ({ ...base, zIndex: 2000 }),
									control: base => ({
										...base,
										borderColor: rollSel ? selColors.okBorder : '#dc3545',
										backgroundColor: rollSel ? selColors.okBg : selColors.errBg,
										minHeight: '34px',
									}),
									singleValue: base => ({ ...base, color: selColors.text ?? base.color }),
									placeholder: base => ({ ...base, color: selColors.errText, fontSize: '12.5px' }),
								}}
							/>
							<i className="uil-ellipsis-h custom-select-btn" role="button" tabIndex={0}
								title={t('select')}
								onClick={() => onOpenRollPicker && onOpenRollPicker(null)}
								onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenRollPicker && onOpenRollPicker(null) } }} />
						</div>
						{rollSel
							? <span className="text-muted" style={{ fontSize: '12px' }}>
								{t('party')}: <b>{formatDate(rollSel.created_at)}</b>
								{rollSel.wms_name ? ` · ${rollSel.wms_name}` : ''}
								{' · '}{t('balance2')}: <b>{formatMoney(rollSel.balance)}</b>
							</span>
							: rollName
							? <span className="text-muted" style={{ fontSize: '12px' }}>({rollName})</span>
							: null}

						{/* ⚠️ TUNIKOFONDA «Ҳажми» — chizmaning ёйилмаси (doska uzunligi),
						    shuning uchun QO'LDA yozilmaydi: chizsangiz o'zi tushadi.
						    Aksessuarda esa hajm mustaqil (2026-08-11). */}
						<span className="text-muted ms-2">{t('size')}:</span>
						<input type="number" step="0.01"
							className={'form-control form-control-sm text-end' + (isDoska ? ' bg-light' : '')}
							style={{ width: '92px' }}
							value={isDoska ? (blank > 0 ? (blank / 1000).toFixed(2) : '') : eq}
							disabled={isDoska}
							title={isDoska ? t('size_from_drawing') : ''}
							onChange={e => { setEq(e.target.value); setDirty(true) }} />
						<span className="text-muted">{t('m')}</span>

						<span className="text-muted ms-2">{t('quantity')}:</span>
						<input type="number" className="form-control form-control-sm text-end"
							style={{ width: '78px' }} value={qty}
							onChange={e => { setQty(e.target.value); setDirty(true) }} />

						{metr > 0 &&
							<span className="ms-auto">{t('metr')}: <b>{metr.toFixed(2)}</b> {t('m')}</span>}
					</div>}

				<div className="row g-0">
					{/* ── maydon ── */}
					<div className="col-lg-8 position-relative border-end" style={{ minHeight: '460px', background: 'var(--dw-paper, #fff)' }}>
						{view === 'draw' && gridOn &&
							<div style={{
								position: 'absolute', inset: 0, pointerEvents: 'none',
								backgroundImage: 'linear-gradient(var(--dw-grid, #f1f0f6) 1px,transparent 1px),linear-gradient(90deg,var(--dw-grid, #f1f0f6) 1px,transparent 1px)',
								backgroundSize: '38px 38px',
							}} />
						}

						{/* chap asboblar */}
						{view === 'draw' &&
							<div className="position-absolute d-flex flex-column gap-1" style={{ top: '12px', left: '12px', zIndex: 4 }}>
								{/* RANG TOMONINI ALMASHTIRISH — nuIT da ham eng tepada turadi.
								    Kvadrat rulonning haqiqiy rangida; chizmadagi strelkalar
								    shu rangda bo'lib, bo'yalgan tomonni ko'rsatib turadi. */}
								<button type="button" className="btn btn-sm btn-light border d-flex align-items-center justify-content-center"
									style={{ width: '34px', height: '31px', marginBottom: '7px' }}
									onClick={() => setColourTop(!colourTop)} title={t('colour_side')}>
									<span style={{
										width: '13px', height: '13px', borderRadius: '3px', background: coilHex,
										border: '1px solid rgba(0,0,0,.35)', display: 'inline-block',
									}} />
									<span style={{ fontSize: '10px', marginLeft: '2px' }}>{colourTop ? '▲' : '▼'}</span>
								</button>
								<button type="button" className={'btn btn-sm ' + (tool === 'sel' ? 'btn-primary' : 'btn-light border')}
									style={{ width: '34px' }} onClick={() => setTool('sel')} title={t('select')}>▣</button>
								<button type="button" className={'btn btn-sm ' + (tool === 'draw' ? 'btn-primary' : 'btn-light border')}
									style={{ width: '34px' }} onClick={() => setTool('draw')} title={t('draw')}>✎</button>
								<button type="button" className="btn btn-sm btn-light border" style={{ width: '34px', marginTop: '7px' }}
									onClick={undo} title="Ctrl+Z">↶</button>
								{/* Noldan boshlash — segmentlarni bittalab o'chirib bo'lmaganda */}
								<button type="button" className="btn btn-sm btn-light border text-danger"
									style={{ width: '34px' }} onClick={clearAll}
									disabled={pts.length < 2} title={t('clear')}>🗑</button>
								<button type="button" className={'btn btn-sm ' + (gridOn ? 'btn-primary' : 'btn-light border')}
									style={{ width: '34px' }} onClick={() => setGridOn(!gridOn)} title={t('grid')}>▦</button>
							</div>
						}

						{/* razvyortka belgisi */}
						{view !== '3d' &&
							<div className="position-absolute bg-white border rounded px-3 py-1"
								style={{ top: '12px', right: '12px', zIndex: 4, fontSize: '12.5px', fontWeight: 700 }}>
								{t('stretch_out')} <span className="text-primary">{blank}</span> {t('mm')}
							</div>
						}

						{/* bo'sh varaq — nima qilish kerakligi yozib qo'yiladi */}
						{view === 'draw' && pts.length < 2 && !preview &&
							<div className="position-absolute text-muted text-center"
								style={{ top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 3,
									fontSize: '13px', lineHeight: 1.7, pointerEvents: 'none' }}>
								<div style={{ fontSize: '32px', opacity: .2, lineHeight: 1 }}>✎</div>
								{t('draw_start_hint')}
							</div>
						}

						{/* xato banneri */}
						{errs.length > 0 && view === 'draw' &&
							<div className="position-absolute bg-danger text-white rounded px-3 py-1"
								style={{ top: '52px', left: '50%', transform: 'translateX(-50%)', zIndex: 5, fontSize: '12px', maxWidth: '80%' }}>
								⚠ {errs[0].msg}
							</div>
						}

						{view === 'draw' &&
							<svg ref={svgRef} viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="xMidYMid meet"
								style={{ display: 'block', width: '100%', height: '460px', touchAction: 'none',
									// surish paytida — qalam yoqilgan bo'lsa ham — «ushlangan» ko'rsatkich
									cursor: panRef.current.on ? 'grabbing' : (tool === 'draw' ? 'crosshair' : 'grab') }}
								onPointerDown={onDown}
								/* g'ildirak bosilganda Chrome ning avto-siljishi yonmasin */
								onAuxClick={e => e.preventDefault()}
								/* G'ILDIRAK = yaqinlashtirish (katak kattalashadi/kichrayadi) */
								onWheel={e => {
									const up = e.deltaY < 0
									setView2(v => ({ ...v, k: Math.max(0.3, Math.min(6, v.k * (up ? 1.12 : 1 / 1.12))) }))
								}}>
								{renderCanvas()}
							</svg>
						}

						{view === '3d' &&
							<canvas ref={canvasRef} width="1400" height="920"
								style={{ display: 'block', width: '100%', height: '460px', cursor: 'grab' }}
								/* ⚠️ e.currentTarget ni setState ICHIDA o'qib bo'lmaydi — React
								   hodisani tozalab yuboradi va u null bo'ladi (oq ekran sababi
								   shu edi, 2026-08-08). Shuning uchun qiymatlar OLDIN olinadi
								   va holat `orbitRef` da saqlanadi. */
								onPointerDown={e => {
									e.currentTarget.setPointerCapture(e.pointerId)
									orbitRef.current = { on: true, x: e.clientX, y: e.clientY }
								}}
								onPointerMove={e => {
									const o = orbitRef.current
									if (!o.on) return
									const dx = e.clientX - o.x, dy = e.clientY - o.y
									orbitRef.current = { on: true, x: e.clientX, y: e.clientY }
									setYaw(y => y + dx * 0.42)
									setPitch(p => Math.max(-86, Math.min(86, p - dy * 0.32)))
								}}
								onPointerUp={() => { orbitRef.current = { on: false, x: 0, y: 0 } }}
								onPointerLeave={() => { orbitRef.current = { on: false, x: 0, y: 0 } }}
								onWheel={e => {
									const up = e.deltaY < 0
									setZoom(z => Math.max(0.25, Math.min(14, z * (up ? 1.14 : 0.88))))
								}}
							/>
						}

						{view === '3d' &&
							<div className="position-absolute d-flex gap-1"
								style={{ bottom: '14px', left: '14px', zIndex: 4 }}>
								<button type="button" className="btn btn-sm btn-light border"
									onClick={() => { setYaw(0); setPitch(0); setZoom(1) }}>{t('front')}</button>
								<button type="button" className="btn btn-sm btn-light border"
									onClick={() => { setYaw(-90); setPitch(0); setZoom(1) }}>{t('side')}</button>
							</div>
						}

						{/* ── RANG IZOHI — «qaysi tomoni bo'yalgan?» degan savolga javob ── */}
						{view === '3d' &&
							<div className="position-absolute bg-white border rounded px-2 py-1"
								style={{ top: '12px', left: '12px', zIndex: 4, fontSize: '12px', lineHeight: 1.7 }}>
								<div className="d-flex align-items-center gap-2">
									<span style={{
										width: '14px', height: '14px', borderRadius: '3px',
										background: coilHex, border: '1px solid rgba(0,0,0,.3)',
									}} />
									<span className="fw-bold">{t('colour_side')}</span>
								</div>
								<div className="d-flex align-items-center gap-2 text-muted">
									<span style={{
										width: '14px', height: '14px', borderRadius: '3px',
										background: STEEL_HEX, border: '1px solid rgba(0,0,0,.22)',
									}} />
									<span>{t('back_side')}</span>
								</div>
								{!hasRal &&
									<div className="text-warning" style={{ fontSize: '11px', maxWidth: '200px', lineHeight: 1.35 }}>
										⚠ {t('roll_colour_not_set')}
									</div>}
							</div>
						}

						{/* ── DUMALOQ ASBOBLAR (nuIT dagidek) — o'ng pastda ── */}
						{true &&
							<div className="position-absolute d-flex gap-2" style={{ bottom: '14px', right: '14px', zIndex: 4 }}>
								{view === 'draw'
									? <>
										<RoundBtn title={t('view_3d')} onClick={() => setView('3d')}>
											<span style={{ fontSize: '11px', fontWeight: 700 }}>3D</span></RoundBtn>
										<RoundBtn title={t('mirror')} onClick={mirror}>⇋</RoundBtn>
										<RoundBtn title={t('rotate_cw')} onClick={() => rotate(90)}>↻</RoundBtn>
										<RoundBtn title={t('rotate_ccw')} onClick={() => rotate(-90)}>↺</RoundBtn>
										<RoundBtn title={t('reset_view')} onClick={() => setView2({ k: 1, tx: 0, ty: 0 })}>✥</RoundBtn>
										<RoundBtn title={t('zoom_in')} onClick={() => zoom2(1.2)}>＋</RoundBtn>
										<RoundBtn title={t('zoom_out')} onClick={() => zoom2(1 / 1.2)}>－</RoundBtn>
									</>
									: <>
										<RoundBtn title={t('view_2d')} onClick={() => setView('draw')}>
											<span style={{ fontSize: '11px', fontWeight: 700 }}>2D</span></RoundBtn>
										<RoundBtn title={t('rotate_ccw')} onClick={() => setYaw(y => y - 20)}>↺</RoundBtn>
										<RoundBtn title={t('rotate_cw')} onClick={() => setYaw(y => y + 20)}>↻</RoundBtn>
										<RoundBtn title={t('reset_view')}
											onClick={() => { setYaw(-32); setPitch(20); setZoom(1) }}>⌂</RoundBtn>
										<RoundBtn title={t('zoom_in')} onClick={() => setZoom(z => Math.min(14, z * 1.2))}>＋</RoundBtn>
										<RoundBtn title={t('zoom_out')} onClick={() => setZoom(z => Math.max(0.25, z / 1.2))}>－</RoundBtn>
									</>
								}
							</div>
						}


						{/* UCH NUQTASIDAGI DOIRA MENYU — qayirma qo'yish */}
						{radial && (() => {
							const svg = svgRef.current
							if (!svg) return null
							const p = svg.createSVGPoint(); p.x = radial.x; p.y = radial.y
							const sc = p.matrixTransform(svg.getScreenCTM())
							const box = svg.parentElement.getBoundingClientRect()
							const L = sc.x - box.left, T = sc.y - box.top
							const dot = {
								width: '38px', height: '38px', borderRadius: '50%', border: 0,
								background: '#2b2438', color: '#fff', fontSize: '15px', position: 'absolute',
								top: '29px', display: 'flex', alignItems: 'center', justifyContent: 'center',
							}
							return (
								<div style={{ position: 'absolute', left: L, top: T, width: '96px', height: '96px', margin: '-48px 0 0 -48px', zIndex: 10 }}>
									<div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#cfe0fa', opacity: .85 }} />
									<button type="button" style={{ ...dot, left: '-3px' }} title={t('hem')}
										onClick={() => { toggleHem(radial.which); setRadial(null) }}>⌒</button>
									<button type="button" style={{ ...dot, right: '-3px' }} title={t('cancel')}
										onClick={() => setRadial(null)}>✕</button>
								</div>
							)
						})()}

						{/* ekranda tahrirlash */}
						{inline &&
							<div className="position-absolute bg-white border border-primary rounded d-flex align-items-center p-1"
								style={{ left: inline.left, top: inline.top, zIndex: 9 }}>
								<input type="number" autoFocus defaultValue={inline.value}
									className="border-0 text-end" style={{ width: '64px', outline: 'none' }}
									onKeyDown={e => {
										if (e.key === 'Enter') applyInline(e.target.value)
										if (e.key === 'Escape') setInline(null)
									}}
									onBlur={e => applyInline(e.target.value)} />
								<span className="text-muted pe-1" style={{ fontSize: '11px' }}>{inline.kind === 'ang' ? '°' : t('mm')}</span>
							</div>
						}
					</div>

					{/* ── o'ng panel ── */}
					<div className="col-lg-4 p-3" style={{ maxHeight: '460px', overflowY: 'auto' }}>
						{bend && hem[bend]
							? <BendPanel t={t} which={bend} hem={hem} pts={pts}
								onClose={() => setBend(null)}
								onDelete={() => toggleHem(bend)}
								onFlip={() => { pushHist(); const r = flipHemDirIn(pts, hem, bend); setPts(r.pts); setHem(r.hem) }}
								onPrev={() => setBend(bend === 'first' ? 'last' : 'first')}
								hasOther={hem[bend === 'first' ? 'last' : 'first']}
								onLen={v => { pushHist(); setPts(setHemLenIn(pts, bend, v)) }} />
							: sel >= 0 && sel < segs.length
							? <SegPanel t={t} i={sel} segs={segs} count={segs.length}
								onClose={() => setSel(-1)}
								onDelete={() => delSeg(sel)}
								onStep={d => setSel(s => Math.max(0, Math.min(segs.length - 1, s + d)))}
								onLen={v => { pushHist(); setPts(setSegLen(pts, sel, v)) }}
								onAng={v => { pushHist(); setPts(setSegAng(pts, sel, v)) }} />
							: <SegmentPanel t={t} pts={pts} segs={segs} hem={hem} sel={sel} errs={errs}
								onLen={(i, v) => { pushHist(); setPts(setSegLen(pts, i, v)) }}
								onAng={(i, v) => { pushHist(); setPts(setSegAng(pts, i, v)) }}
								onDir0={v => { pushHist(); setPts(setDir0(pts, v)) }}
								onDel={delSeg}
								onHem={w => toggleHem(w)} />
						}
					</div>
				</div>

				{/* ── rulon kesish ── */}
				<div className="border-top px-3 py-2 bg-light">
					<div className="d-flex align-items-center gap-3 flex-wrap" style={{ fontSize: '12.5px' }}>
						<span>{t('stretch_out')}: <b>{blank}</b> {t('mm')}</span>
						{/* ⚠️ DOSKA REJIMIDA «дona / Сарф / Сарфгача чўзиш / Қолдиқ»
						    KO'RSATILMAYDI. Bularning hammasi AKSESSUAR mantig'i:
						    «razvyortka rulon eniga necha marta sig'adi, ortgani nima
						    bo'ladi». Doskada esa ёйилма — UZUNLIK, rulon eniga
						    sig'ish masalasi umuman yo'q; kenglik «неча бўлак» dan
						    chiqadi (2026-08-11, foydalanuvchi: «бу ерда қолдиқ
						    муаммо йўқ, чунки бу ҳажм»). */}
						{!isDoska &&
						<span>{t('pieces')}: <b>{calc.over ? 0 : calc.fits}</b></span>}
						{/* RASXOD — avtomat hisoblanadi, LEKIN qo'lda yozsa ham bo'ladi.
						    Yo'qotishni boshqa joyga «yedirish» kerak bo'lganda ishlatiladi. */}
						{!isDoska &&
						<span className="d-inline-flex align-items-center gap-1">
							<span className={calc.manual ? 'text-warning' : 'text-success'}>{t('consumption')}:</span>
							<input type="number" step="0.1"
								className={'form-control form-control-sm text-end py-0 ' + (calc.manual ? 'border-warning' : '')}
								style={{ width: '82px', height: '26px', fontWeight: 700 }}
								value={calc.cons.toFixed(1)}
								onChange={e => {
									const v = Number(e.target.value)
									setConsManual(v > 0 ? v : null); setDirty(true)
								}} />
							<span className="text-muted">{t('mm')}</span>
							{calc.manual &&
								<button type="button" className="btn btn-sm btn-link p-0 px-1 text-muted"
									title={t('auto')} style={{ fontSize: '13px' }}
									onClick={() => { setConsManual(null); setDirty(true) }}>↺</button>}
						</span>}
						{/* Ortiqcha mm ni chiqindiga bermay, tanlangan bo'lakka qo'shib yuborish */}
						{!isDoska && leftover >= 1 &&
							<span className="d-inline-flex align-items-center gap-1">
								<span className="text-primary">↳ {t('feed_leftover')} (<b>+{leftover}</b> {t('mm')}):</span>
								<select className="form-select form-select-sm py-0" value=""
									style={{ width: '132px', height: '26px', fontSize: '12px' }}
									onChange={e => { if (e.target.value !== '') feedLeftover(Number(e.target.value)) }}>
									<option value="">— {t('segment')} —</option>
									{segs.map((sg, i) =>
										<option key={i} value={i}>
											{i + 1}: {sg.len} → {Math.floor(sg.len + calc.cons - blank)} {t('mm')}
										</option>)}
								</select>
							</span>}
						{/* Qoldiq 1 mm dan kichik — cho'zib bo'lmaydi. Ilgari bu holatda
						    ekranda HECH NARSA chiqmasdi va foydalanuvchi «tugma
						    ishlamayapti» deb o'ylardi. Endi sababi yozib turadi. */}
						{!isDoska && calc.cons > blank && leftover < 1 &&
							<span className="text-muted" style={{ fontSize: '12px' }}>
								↳ {t('feed_leftover')}: +{(calc.cons - blank).toFixed(1)} {t('mm')} —
								{' '}{t('less_than_mm_hint')}
							</span>}
						{/* ═══ TUNIKOFON: LISTNI NECHA BO'LAKKA BO'LAMIZ ═══
						    Kesilgan list (rulon eni bo'ylab) 1/2/3/4 ga bo'linadi —
						    doskaning KENGLIGI shundan chiqadi. Uzunlik esa
						    chizmaning ёйилмасидан (2026-08-11, foydalanuvchi tartibi). */}
						{isDoska &&
							<span className="d-inline-flex align-items-center gap-2">
								<span className="text-primary">{t('pieces_per_sheet')}:</span>
								<span className="btn-group btn-group-sm">
									{[1, 2, 3, 4].map(n => (
										<button key={n} type="button"
											className={'btn ' + (bolak === n ? 'btn-primary' : 'btn-outline-primary')}
											style={{ padding: '1px 10px', fontSize: '12.5px' }}
											onClick={() => { setBolak(n); setDirty(true) }}>{n}</button>
									))}
								</span>
								<span className="text-muted" style={{ fontSize: '12px' }}>
									→ {(tuniW * 1000).toFixed(0)} {t('mm')}
								</span>
							</span>}

						{/* ⚠️ DOSKADA «Нархи» KO'RSATILMAYDI: kv.metr narxi kenglikka
						    bog'liq emas, ya'ni bo'lakni o'zgartirsangiz ham o'zgarmaydi —
						    yonma-yon turgani chalg'itardi (2026-08-11, user aytdi).
						    Kv.метр va Рулон м esa o'zgaradi, ular qoladi. */}
						{!templateMode &&
							<span className="ms-auto">
								{isDoska
									? <>{t('sq_m')}: <b>{tuniKv.toFixed(2)}</b> · {t('roll_m')}: <b>{tuniRollM.toFixed(2)}</b></>
									: <>{t('price')}: <b>{formatMoney(price)}</b> · {t('roll_m')}: <b>{rollM.toFixed(2)}</b></>}
							</span>}
					</div>

					{/* Tunikofonda ёйилма — UZUNLIK. Buni ochiq aytmasak usta uni
					    kenglik deb o'ylab qoladi (aksessuarda shunday edi). */}
					{isDoska && blank > 0 &&
						<div className="text-muted mt-2 pt-2 border-top" style={{ fontSize: '12px' }}>
							{t('tuni_draw_hint', {
								uzunlik: (blank / 1000).toFixed(2),
								bolak,
								kenglik: (tuniW * 1000).toFixed(0),
							})}
						</div>}

					{/* ── QOLDIQ — FAQAT MA'LUMOT ──
					    Mijoz to'liq ulushga to'laydi, ortgan tasma unga beriladi.
					    ⚠️ Ilgari bu yerda «Мижозга / Омборга / Чиқинди» tanlovi va
					       omborda tasma yuritish bor edi — foydalanuvchi 2026-08-09 da
					       olib tashlashni so'radi: amalda qoldiq mijozga beriladi,
					       kerak bo'lganda «xizmat aksessuar» deb yozib qo'yiladi.
					       Sodda va ishonchli. */}
					{/* ⚠️ DOSKADA QOLDIQ YO'Q — ёйилма rulon eniga sig'ish masalasi
					    emas, u UZUNLIK. Foydalanuvchi: «бу ерда қолдиқ муаммо йўқ,
					    чунки бу ҳажм» (2026-08-11). */}
					{!isDoska && !templateMode && !calc.over && calc.remnant > 0.5 &&
						<div className="d-flex align-items-center gap-2 flex-wrap mt-2 pt-2 border-top"
							style={{ fontSize: '12.5px' }}>
							<span>{t('remnant')}: <b className="text-secondary">
								{calc.remnant.toFixed(0)}</b> {t('mm')} {lenKnown
								? <>× {remnantLenM.toFixed(2)} {t('m')}</>
								: <span className="text-muted">({t('length_after_qty')})</span>}</span>
							<span className="text-muted" style={{ fontSize: '11.5px' }}>
								{t('remnant_hint_client', { w: calc.share.toFixed(0) })}
							</span>
						</div>}

					{calc.over &&
						<div className="text-danger mt-1" style={{ fontSize: '12px' }}>
							⚠ {t('draw_too_wide')} ({blank} &gt; {rollW} {t('mm')})
						</div>}
				</div>
			</Modal.Body>

			<Modal.Footer>
				<button type="button" className="btn btn-outline-secondary" onClick={askClose}>{t('cancel')}</button>
				<button type="button" className="btn btn-primary"
					disabled={pts.length < 2 || calc.over || errs.length > 0} onClick={doSave}>
					💾 {t('save')}
				</button>
			</Modal.Footer>


		</Modal>
	)
}

/* ═════════ dumaloq asbob tugmasi ═════════ */
function RoundBtn({ title, onClick, children }) {
	return (
		<button type="button" title={title} onClick={onClick}
			className="btn btn-light p-0 d-flex align-items-center justify-content-center"
			/* ⚠️ Rang inline yozilgani uchun `.modal .btn-light` qoidasi buni
			   YENGA OLMAYDI — qorong'i mavzuda tugmalar oq dumaloq bo'lib qolardi
			   va ichidagi belgilar ko'rinmasdi (foydalanuvchi surati 2026-08-14).
			   CSS o'zgaruvchisi + zaxira: yoruq mavzuda asl rang saqlanadi. */
			style={{
				width: '36px', height: '36px', borderRadius: '50%', fontSize: '15px',
				border: '1px solid var(--dk-border-2, #e6e6ee)',
				boxShadow: '0 1px 5px rgba(0,0,0,.16)',
				background: 'var(--dk-surface-3, #f4f4f7)',
				color: 'var(--dk-text, inherit)',
			}}>
			{children}
		</button>
	)
}

/* ═════════ o'ng panel: segmentlar ═════════ */
function SegmentPanel({ t, pts, segs, hem, sel, errs, onLen, onAng, onDir0, onDel, onHem }) {
	const errOf = i => errs.find(e => e.i === i)
	const d0 = pts.length > 1
		? Math.round(Math.atan2(pts[1].y - pts[0].y, pts[1].x - pts[0].x) * 180 / Math.PI) : 0
	const isHem = i => (i === 0 && hem.first) || (i === pts.length - 2 && hem.last)

	return (
		<>
			<div className="text-muted fw-bold mb-2" style={{ fontSize: '11.5px', letterSpacing: '.05em' }}>
				{t('segments').toUpperCase()}
			</div>
			{/* «Boshiga/oxiriga qo'shish» tugmalari OLIB TASHLANDI (2026-08-09):
			    segment maydonning O'ZIDA sichqoncha bilan qo'shiladi (chizish asbobi
			    yoki uchidagi belgini sudrash), panelda ikkilanish yaratardi. */}
			<div className="d-flex align-items-center gap-2 mb-2">
				<span className="text-muted" style={{ fontSize: '11px', width: '66px' }}>{t('direction')}</span>
				<input type="number" className="form-control form-control-sm text-end" style={{ width: '70px' }}
					value={d0} step="5" onChange={e => onDir0(Number(e.target.value) || 0)} />
				<span className="text-muted" style={{ fontSize: '11px' }}>°</span>
			</div>

			{segs.map((sg, i) => {
				const hemHere = isHem(i), edge = i === 0 || i === segs.length - 1, er = errOf(i)
				return (
					<Fragment key={i}>
						{sg.ang != null && !hemHere &&
							<div className="d-flex align-items-center gap-2 mb-1 ms-4">
								<input type="number" className="form-control form-control-sm text-end"
									style={{ width: '62px', background: '#fdf9ef', borderColor: '#e8d9b8', color: '#8a6412', fontWeight: 600 }}
									value={toBend(sg.ang)} min="1" max="179"
									onChange={e => onAng(i, fromBend(e.target.value))} />
								<span className="text-muted" style={{ fontSize: '11px' }}>°</span>
							</div>
						}
						<div className={'d-flex align-items-center gap-1 mb-1 ' + (sel === i ? 'bg-light rounded' : '')}>
							<span className="badge bg-light text-muted" style={{ width: '22px' }}>{hemHere ? '↩' : i + 1}</span>
							<input type="number" className={'form-control form-control-sm text-end ' + (er ? 'is-invalid' : '')}
								value={sg.len} min="1" onChange={e => onLen(i, Math.max(1, Number(e.target.value) || 1))} />
							<span className="text-muted" style={{ fontSize: '10.5px' }}>{t('mm')}</span>
							{edge &&
								<button type="button" className={'btn btn-sm ' + (hemHere ? 'btn-success' : 'btn-light border')}
									style={{ padding: '2px 7px' }} title={t('hem')}
									onClick={() => onHem(i === 0 ? 'first' : 'last')}>↩</button>}
							{/* oxirgi segmentda ✕ butun chizmani tozalaydi (delSeg → clearAll) */}
							<button type="button" className="btn btn-sm btn-link text-danger p-0 px-1"
								onClick={() => onDel(i)}>✕</button>
						</div>
						{er && <div className="text-danger ms-4 mb-1" style={{ fontSize: '11px' }}>⚠ {er.msg}</div>}
					</Fragment>
				)
			})}

		</>
	)
}

/* ═════════ o'ng panel: SEGMENT (nuIT «Segment Details») ═════════ */
function PanelHead({ title, onDelete, onPrev, onNext, canPrev, canNext, onClose }) {
	const btn = { width: '34px', height: '34px', padding: 0 }
	return (
		<div className="d-flex align-items-center mb-3">
			<b style={{ fontSize: '16px' }}>{title}</b>
			<span className="ms-auto d-flex gap-1">
				<button type="button" className="btn btn-sm btn-light border" style={btn} onClick={onDelete}>🗑</button>
				<button type="button" className="btn btn-sm btn-light border" style={btn} onClick={onPrev} disabled={!canPrev}>←</button>
				<button type="button" className="btn btn-sm btn-light border" style={btn} onClick={onNext} disabled={!canNext}>→</button>
				<button type="button" className="btn btn-sm btn-light border" style={btn} onClick={onClose}>✕</button>
			</span>
		</div>
	)
}

function SegPanel({ t, i, segs, count, onClose, onDelete, onStep, onLen, onAng }) {
	const sg = segs[i]
	return (
		<>
			<PanelHead title={t('segment_details')} onDelete={onDelete} onClose={onClose}
				onPrev={() => onStep(-1)} onNext={() => onStep(1)} canPrev={i > 0} canNext={i < count - 1} />
			<div className="border rounded p-3">
				<div className="fw-bold mb-3" style={{ fontSize: '13px' }}>{t('edit_segment')}</div>

				<div className="text-muted mb-1" style={{ fontSize: '11.5px' }}>{t('length')}</div>
				<div className="d-flex align-items-center gap-2 mb-3">
					<input type="number" className="form-control form-control-sm text-end" style={{ width: '96px' }}
						value={sg.len} min="1" onChange={e => onLen(Math.max(1, Number(e.target.value) || 1))} />
					<span className="text-muted" style={{ fontSize: '11px' }}>{t('mm')}</span>
				</div>

				{sg.ang != null && <>
					<div className="text-muted mb-1" style={{ fontSize: '11.5px' }}>{t('angle')}</div>
					<div className="d-flex align-items-center gap-2">
						<input type="number" className="form-control form-control-sm text-end" style={{ width: '96px' }}
							value={toBend(sg.ang)} min="1" max="179"
							onChange={e => onAng(fromBend(e.target.value))} />
						<span className="text-muted" style={{ fontSize: '11px' }}>°</span>
					</div>
				</>}
			</div>
		</>
	)
}

/* ═════════ o'ng panel: BUKILISH (nuIT «Bend Details») ═════════ */
function BendPanel({ t, which, hem, pts, onClose, onDelete, onFlip, onPrev, hasOther, onLen }) {
	const n = pts.length
	const len = which === 'first'
		? Math.round(Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y))
		: Math.round(Math.hypot(pts[n - 1].x - pts[n - 2].x, pts[n - 1].y - pts[n - 2].y))
	return (
		<>
			<PanelHead title={t('bend_details')} onDelete={onDelete} onClose={onClose}
				onPrev={onPrev} onNext={onPrev} canPrev={hasOther} canNext={hasOther} />
			<div className="border rounded p-3">
				<div className="fw-bold mb-3" style={{ fontSize: '13px' }}>{t('edit_hem')}</div>

				<div className="text-muted mb-1" style={{ fontSize: '11.5px' }}>{t('hem_type')}</div>
				<div className="d-flex border rounded overflow-hidden mb-3">
					<button type="button" className="btn border-0 rounded-0 flex-fill"
						style={{ background: '#eef6e0', color: '#3d6b0f', fontSize: '17px' }}>⌒</button>
				</div>

				<div className="text-muted mb-1" style={{ fontSize: '11.5px' }}>{t('direction')}</div>
				<button type="button" className="btn btn-light border rounded-circle mb-3"
					style={{ width: '42px', height: '42px' }} onClick={onFlip} title={t('direction')}>↕</button>

				<div className="text-muted mb-1" style={{ fontSize: '11.5px' }}>{t('length')}</div>
				<div className="d-flex align-items-center gap-2">
					<input type="number" className="form-control form-control-sm text-end" style={{ width: '96px' }}
						value={len} onChange={e => onLen(Math.max(3, Number(e.target.value) || HEM_LEN))} />
					<span className="text-muted" style={{ fontSize: '11px' }}>{t('mm')}</span>
				</div>
			</div>
		</>
	)
}

/* ═════════ 3D ═════════
   Kutubxonasiz — oddiy canvas ustida. Haqiqiyroq ko'rinishi uchun:
   (a) studiya aks etishi — metall tepadagi yorug' chiziqni aks ettiradi;
   (b) tunuka QALINLIGI rulon nomidan olinadi (0.4 mm va h.k.);
   (c) qora konturlar yo'q — ular «multfilm» ko'rinishini berardi;
   (d) soya kichraytirilgan maydonda chizilib cho'ziladi — 16 barobar arzon. */
function render3d(cvs, pts, hem, colourTop, yaw, pitch, zoom, lengthMM, rollRal, thickMM) {
	const ctx = cvs.getContext('2d'), W = cvs.width, H = cvs.height
	ctx.clearRect(0, 0, W, H)
	/* Studiya foni — yumshoq radial, markazi yorug'.
	   ⚠️ Bu KANVAS ichida chiziladi, ya'ni CSS unga UMUMAN ta'sir qilmaydi
	      (`--dw-paper` faqat kanvas ORTIDAGI div ni bo'yaydi). Shu sabab
	      qorong'i mavzuda 3D ko'rinish oppoq quti bo'lib turardi
	      (foydalanuvchi surati 2026-08-14). Mavzuni shu yerda o'qiymiz. */
	const dark3d = typeof document !== 'undefined'
		&& document.documentElement.getAttribute('data-theme') === 'dark'
	const bg = ctx.createRadialGradient(W * .46, H * .30, 10, W * .5, H * .5, Math.max(W, H) * .78)
	if (dark3d) {
		bg.addColorStop(0, '#243352'); bg.addColorStop(.45, '#1c2743')
		bg.addColorStop(.8, '#161f36'); bg.addColorStop(1, '#101828')
	} else {
		bg.addColorStop(0, '#ffffff'); bg.addColorStop(.45, '#f2f4f8')
		bg.addColorStop(.8, '#e4e8ef'); bg.addColorStop(1, '#d3d9e3')
	}
	ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H)

	if (pts.length < 2) return
	const span = blankOf(pts) || 100
	// Uzunlikni cheklaymiz: 3 m li bo'lak ekranda ingichka tayoqqa aylanib,
	// profil shakli umuman ko'rinmay qolardi. Bu — shakl ko'rgichi, o'lchov emas.
	const L = Math.min(Math.max(lengthMM || 3000, 100), span * 1.8)

	/* QALINLIK. Haqiqiy qiymat ishlatiladi (rulon nomidan), lekin 0.4 mm
	   400 mm li profilda ekranda yarim pikselga tushib, umuman ko'rinmay qoladi.
	   Shuning uchun chegara MILLIMETRDA emas, PIKSELDA qo'yiladi: chiziq hech
	   bo'lmasa ~1.8 piksel bo'lsin. Yaqinlashtirilganda (＋ tugmasi) bu chegara
	   o'z-o'zidan pasayadi va qalinlik HAQIQIY o'lchovda chiziladi —
	   shu sabab 0.4 mm bilan 2 mm bir xil ko'rinmaydi. */
	const build = T => {
		const lp = crossSection(pts, T / 2)
		if (!lp.length) return null
		const ax = lp.reduce((a, p) => a + p.x, 0) / lp.length
		const ay = lp.reduce((a, p) => a + p.y, 0) / lp.length
		let r = 0
		for (const p of lp) r = Math.max(r, Math.hypot(p.x - ax, p.y - ay, L / 2))
		return { loop: lp, cx: ax, cy: ay, k: Math.min(W, H) * 0.46 / Math.max(r, 1) * zoom }
	}
	const realT = Number(thickMM) > 0 ? Number(thickMM) : 0.45
	let g = build(realT)
	if (!g) return
	const minT = 1.2 / g.k
	if (realT < minT) { const g2 = build(minT); if (g2) g = g2 }
	const { loop, cx, cy, k } = g

	const ry = yaw * Math.PI / 180, rp = pitch * Math.PI / 180
	const cY = Math.cos(ry), sY = Math.sin(ry), cP = Math.cos(rp), sP = Math.sin(rp)
	const rot = p => {
		const x = p.x - cx, y = p.y - cy, z = p.z
		const x1 = x * cY + z * sY, z1 = -x * sY + z * cY
		return { x: x1, y: y * cP - z1 * sP, z: y * sP + z1 * cP }
	}
	const rotN = n => {
		const x1 = n.x * cY, z1 = -n.x * sY
		return { x: x1, y: n.y * cP - z1 * sP, z: n.y * sP + z1 * cP }
	}
	const proj = p => ({ x: W / 2 + p.x * k, y: H / 2 + p.y * k, z: p.z })

	const all = loop.map(p => rot({ x: p.x, y: p.y, z: -L / 2 }))
	const groundY = Math.max(...all.map(p => p.y)) + 10
	/* Soya — 4 marta KICHRAYTIRILGAN yordamchi maydonda chiziladi, keyin cho'ziladi.
	   Sabab: `blur(46px)` ni 1400×920 da bajarish videokartasiz kompyuterda og'ir
	   (bu yerda 16 barobar kam piksel). Xira dog' bo'lgani uchun cho'zilishi bilinmaydi. */
	const SS = 4
	let sCv = null
	const shade = (spread, alpha, blur) => {
		if (!sCv) {
			sCv = document.createElement('canvas')
			sCv.width = Math.max(1, Math.round(W / SS)); sCv.height = Math.max(1, Math.round(H / SS))
		}
		const sx = sCv.getContext('2d')
		sx.clearRect(0, 0, sCv.width, sCv.height)
		sx.save(); sx.filter = `blur(${blur / SS}px)`; sx.fillStyle = `rgba(44,50,66,${alpha})`
		sx.beginPath()
		const flat = z => loop.map(p => {
			const q = rot({ x: p.x, y: p.y, z })
			const r = proj({ x: q.x + (groundY - q.y) * spread, y: groundY, z: q.z })
			return { x: r.x / SS, y: r.y / SS }
		})
		const hull = flat(-L / 2).concat(flat(L / 2).reverse())
		sx.moveTo(hull[0].x, hull[0].y)
		for (const p of hull) sx.lineTo(p.x, p.y)
		sx.closePath(); sx.fill(); sx.restore()
		ctx.drawImage(sCv, 0, 0, W, H)
	}
	shade(.62, .15, 46); shade(.30, .25, 15)

	const N = loop.length, LIGHT = { x: -.42, y: -.74, z: .52 }
	const vn = []
	for (let i = 0; i < N; i++) {
		const p = loop[(i - 1 + N) % N], c = loop[i], nn = loop[(i + 1) % N]
		let nx = 0, ny = 0
		let dx = c.x - p.x, dy = c.y - p.y, L1 = Math.hypot(dx, dy) || 1; nx += dy / L1; ny += -dx / L1
		dx = nn.x - c.x; dy = nn.y - c.y; const L2 = Math.hypot(dx, dy) || 1; nx += dy / L2; ny += -dx / L2
		const L3 = Math.hypot(nx, ny) || 1
		vn.push({ x: nx / L3, y: ny / L3 })
	}
	const KEY = [255, 253, 246]
	const STEEL = [176, 184, 196]
	// RULON RANGI — Nastroykada yozilgan RAL kodidan. Topilmasa po'lat rangi.
	// Ranglangan tomon shu rangda, orqa tomon esa och po'lat bo'lib qoladi.
	const COIL = hexToRgb(ralToHex(rollRal)) || [150, 165, 150]
	const clamp = (v, a, b) => Math.max(a, Math.min(b, v))

	/* STUDIYA AKS ETISHI — metallni metall qiladigan narsa shu.
	   Yassi «ambient» o'rniga: pastda to'q pol, o'rtada kulrang devor,
	   tepada yorqin chiroq chizig'i. Yuza qaysi tomonga qaragan bo'lsa,
	   o'shanisini aks ettiradi — shundan tabiiy yorug'-soya kontrasti chiqadi. */
	const envAt = t => 0.30 + 0.52 * t
		+ 0.95 * Math.exp(-Math.pow((t - 0.83) / 0.115, 2))     // shift chirog'i
		+ 0.18 * Math.exp(-Math.pow((t - 0.34) / 0.16, 2))      // devordan qaytgan yumshoq nur

	const sh = (n, colour) => {
		const r = rotN(n)
		const t = clamp((-r.y + 1) / 2, 0, 1)
		const e = envAt(t)
		const diff = Math.max(0, -(r.x * LIGHT.x + r.y * LIGHT.y + r.z * LIGHT.z))
		const hx = LIGHT.x, hy = LIGHT.y, hz = LIGHT.z - 1, hl = Math.hypot(hx, hy, hz) || 1
		const nh = Math.max(0, -(r.x * hx / hl + r.y * hy / hl + r.z * hz / hl))
		const spec = Math.pow(nh, 64) * 1.05 + Math.pow(nh, 9) * 0.15   // o'tkir + keng yaltirash
		const fres = Math.pow(1 - Math.min(1, Math.abs(r.z)), 4) * 0.26  // qirradagi nur
		const painted = colour && colourTop
		const base = painted ? COIL : STEEL
		// Bo'yalgan tomon kamroq aks ettiradi (mat lak), yalang'och po'lat — ko'proq
		const mRefl = painted ? 0.46 : 0.80
		const mDiff = painted ? 0.62 : 0.34
		return `rgb(${[0, 1, 2].map(i => clamp(
			base[i] * (e * mRefl + diff * mDiff + 0.06) + KEY[i] * spec * (painted ? 0.55 : 0.95) + 252 * fres,
			0, 255) | 0).join(',')})`
	}

	const faces = []
	for (let i = 0; i < N; i++) {
		const j = (i + 1) % N
		const colour = loop[i].c === 1 && loop[j].c === 1
		faces.push({
			q: [rot({ x: loop[i].x, y: loop[i].y, z: -L / 2 }), rot({ x: loop[j].x, y: loop[j].y, z: -L / 2 }),
				rot({ x: loop[j].x, y: loop[j].y, z: L / 2 }), rot({ x: loop[i].x, y: loop[i].y, z: L / 2 })],
			c1: sh(vn[i], colour), c2: sh(vn[j], colour),
			// oraliq rang — burchak yumaloq bo'lganda yaltirash chizig'i chiqadi
			cm: sh({ x: (vn[i].x + vn[j].x) / 2, y: (vn[i].y + vn[j].y) / 2 }, colour),
		})
	}
	// KESILGAN UCHLARI — bo'yalmagan yalang'och po'lat, shuning uchun ochroq.
	// Ekstruziya o'qi bo'yicha normal bilan soyalanadi (aylantirilganda o'zgaradi).
	faces.push({ q: loop.map(p => rot({ x: p.x, y: p.y, z: -L / 2 })), cap: true, nz: -1 })
	faces.push({ q: loop.map(p => rot({ x: p.x, y: p.y, z: L / 2 })), cap: true, nz: 1 })
	const capShade = nz => {
		const r = rotN({ x: 0, y: 0, z: nz })
		const t = clamp((-r.y + 1) / 2, 0, 1)
		const v = clamp(150 + envAt(t) * 62 + Math.max(0, -r.z) * 26, 90, 246) | 0
		return `rgb(${v},${clamp(v + 3, 0, 255) | 0},${clamp(v + 10, 0, 255) | 0})`
	}
	for (const f of faces) {
		if (f.cap) f.c1 = capShade(f.nz)
		f.P = f.q.map(proj)
		f.depth = f.q.reduce((a, p) => a + p.z, 0) / f.q.length
	}
	faces.sort((a, b) => a.depth - b.depth)

	ctx.lineJoin = 'round'
	for (const f of faces) {
		ctx.beginPath(); ctx.moveTo(f.P[0].x, f.P[0].y)
		for (let i = 1; i < f.P.length; i++) ctx.lineTo(f.P[i].x, f.P[i].y)
		ctx.closePath()
		let paint
		if (f.cap) paint = f.c1
		else {
			const m1 = { x: (f.P[0].x + f.P[3].x) / 2, y: (f.P[0].y + f.P[3].y) / 2 }
			const m2 = { x: (f.P[1].x + f.P[2].x) / 2, y: (f.P[1].y + f.P[2].y) / 2 }
			const g = ctx.createLinearGradient(m1.x, m1.y, m2.x, m2.y)
			g.addColorStop(0, f.c1); g.addColorStop(.5, f.cm); g.addColorStop(1, f.c2)
			paint = g
		}
		ctx.fillStyle = paint
		ctx.fill()
		// ⚠️ Konturni O'Z rangi bilan chizamiz. Qora chiziq «multfilm» ko'rinishini
		//    berardi; chizmasak esa qo'shni yoqlar orasida oq tirqish qoladi.
		ctx.strokeStyle = f.cap ? f.c1 : f.cm
		ctx.lineWidth = 1.15
		ctx.stroke()
	}
}

export default DrawingModal
